import React, { FC } from 'react';
import { Autocomplete, Box, FormLabel, InputAdornment, TextField, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import styles from '../CustomAutoSelect/CustomAutoSelect.module.scss';

interface LocationOption {
  name: string;
  iata_code: string;
}

interface LocationAutoSelectProps {
  options: LocationOption[];
  customClass?: string;
  compactForMobile?: boolean;
  inputLabel: string;
  handleChange: (event: React.SyntheticEvent<Element, Event>, value: LocationOption | null) => void;
  onInputChange: (event: any, inputValue: any) => void;
  value?: LocationOption | null;
  placeholder?: string;
  startAdornment?: React.ReactNode;
  invalid?: boolean;
  disableClearable?: boolean;
}

const LocationAutoSelect: FC<LocationAutoSelectProps> = ({
  options,
  customClass,
  compactForMobile = false,
  inputLabel,
  handleChange,
  value,
  onInputChange,
  placeholder,
  startAdornment,
  invalid = false,
  disableClearable = false,
}) => {
  return (
    <Box
      id={styles.customSelectContainer}
      className={customClass ?? ''}
      data-compact-mobile={compactForMobile ? 'true' : undefined}
    >
      <FormLabel className={styles.customSelectLabel}>
        <Typography variant="h11Semibold" color="#222B3A">{inputLabel}</Typography>
      </FormLabel>
      <Autocomplete
        disablePortal
        options={options}
        onChange={handleChange}
        onInputChange={onInputChange}
        className={`${styles.customInput} ${startAdornment ? styles.autocompleteWithLead : ''}`.trim()}
        popupIcon={<ExpandMoreIcon />}
        disableClearable={disableClearable}
        value={value}
        getOptionLabel={(option: LocationOption) => option.name}
        isOptionEqualToValue={(option, value) => option.iata_code === value.iata_code}
        filterOptions={list => list}
        sx={{ width: '100%', minWidth: 0 }}
        renderInput={params => {
          const { InputProps, ...textFieldRest } = params;
          return (
            <TextField
              {...textFieldRest}
              error={invalid}
              placeholder={placeholder}
              InputLabelProps={{ shrink: false }}
              InputProps={{
                ...InputProps,
                startAdornment: startAdornment ? (
                  <>
                    <InputAdornment
                      position="start"
                      sx={{
                        margin: 0,
                        maxHeight: 'none',
                        '&.MuiInputAdornment-positionStart': { marginRight: 0, marginInlineEnd: 0 },
                      }}
                    >
                      {startAdornment}
                    </InputAdornment>
                    {InputProps.startAdornment}
                  </>
                ) : (
                  InputProps.startAdornment
                ),
              }}
            />
          );
        }}
        renderOption={(props, option) => {
          const { key, ...optionLiProps } = props;
          return (
            <Box
              key={key}
              component="li"
              {...optionLiProps}
            >
              <Box>
                <Typography variant="body1">{option.name}</Typography>
                <Typography
                  variant="caption"
                  sx={{ color: '#666' }}
                >
                  {option.iata_code}
                </Typography>
              </Box>
            </Box>
          );
        }}
      />
    </Box>
  );
};

export default LocationAutoSelect;
